import React, {
  memo,
  useEffect,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ClockCircleOutlined } from '@ant-design/icons';
import Chart from 'react-apexcharts';

import MapQueryTypes from '../../../services/query-types/map';
import { getSamplingData, parameterConfigurations } from '../../../services/dataset.service';
import { brandingConfig } from '../../../config/index';
import './OmicsDatasetSummary.scss';
import useParameterGroups from '../../../config/useParameterGroups';

const OMICS_ABUNDANCE_NEGLIGIBLE_THRESHOLD = 0.0001;
const OMICS_ABUNDANCE_OTHER_THRESHOLD = 1;
const OMICS_ABUNDANCE_DISPLAY_THRESHOLD = 0.01;

const OmicsDatasetSummary = (props) => {
  const {
    info, // Do what you do to this here in DatasetDetail to selectedPlatform
    filters,
    parameters,
  } = props;
  const { properties } = info;

  const [chartData, setChartData] = useState([]);
  const [chartTime, setChartTime] = useState('');
  const [chartLabels, setChartLabels] = useState([]);
  const [startDate, setStartDate] = useState(filters.startDate.format('YYYY-MM-DD'));
  const { parameterGroups } = useParameterGroups();

  useEffect(() => {
    if (properties.observations) {
      setStartDate(moment(properties.observations[0].obs_timestamp).format('YYYY-MM-DD'));
    }
  }, [properties?.observations]);

  const obsDatasetId = properties.obs_dataset_id;

  const {
    data: observationalGeoDataResult,
    isLoading: observationalGeoDataIsLoading,
  } = useQuery(
    [MapQueryTypes.REST_OBSERVATIONAL_V2_DATA_OMICS, startDate, obsDatasetId],
    () => getSamplingData({ startDate, obsDatasetId }), {
      refetchOnWindowFocus: false,
      enabled: (!!startDate && !!obsDatasetId),
    },
  );

  const {
    data: parameterConfigurationsResult,
  } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, { refetchOnWindowFocus: false });

  const chartOptions = {
    legend: {
      height: '100%',
      floating: true,
    },
    labels: chartLabels,
  };

  const validateOmicsData = (value, parameter) => {
    const omicsGroup = parameterGroups.find((g) => g.name === 'omicsOrganismAbundances');

    if (omicsGroup.standard_names.includes(parameter.standard_name) && value > OMICS_ABUNDANCE_NEGLIGIBLE_THRESHOLD) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (observationalGeoDataResult && parameters && parameterConfigurationsResult) {
      // obs-geo returns one entry per requested obs_dataset_id, each with its own
      // per-parameter observations array. Each parameter's observations are ordered
      // latest-first, so observations[0] is that parameter's most recent sample
      // (unlike /obs, there's no single shared timestamps array to index into).
      const parametersData = observationalGeoDataResult?.[0]?.parameters ?? [];
      const data = [];
      const otherData = [];
      const dataLabels = [];
      let latestTimestamp = null;

      parametersData.forEach(({ parameter_id: parameterId, observations }) => {
        const latestObs = observations?.[0];
        if (!latestObs) return;

        if (!latestTimestamp || latestObs.timestamp > latestTimestamp) {
          latestTimestamp = latestObs.timestamp;
        }

        const obsValue = latestObs.value;
        const parameter = parameters.find((p) => p.parameter_id === parameterId);

        if (parameter && validateOmicsData(obsValue, parameter)) {
          const parameterConfig = parameterConfigurationsResult?.find((p) => p.standard_name === parameter.standard_name);
          const displayName = parameterConfig.display_name.en;
          const isCurrentFilter = filters.filter ? displayName.includes(filters.filter) : false;

          if (obsValue < OMICS_ABUNDANCE_OTHER_THRESHOLD && !isCurrentFilter) {
            otherData.push(obsValue);
          } else {
            data.push(obsValue);

            let label = obsValue < OMICS_ABUNDANCE_DISPLAY_THRESHOLD ? `${displayName} <0.01%` : `${displayName} ${obsValue.toFixed(2)}%`;

            if (isCurrentFilter) {
              label = `<strong>${label}</strong>`;
            }

            if (parameterConfig) {
              dataLabels.push(label);
            }
          }
        }
      });

      if (otherData.length > 0) {
        const otherSum = otherData.reduce((a, b) => a + b, 0);
        data.push(otherSum);
        const otherLabel = otherSum < OMICS_ABUNDANCE_DISPLAY_THRESHOLD ? 'Other <0.01%' : `Other ${otherSum.toFixed(2)}%`;
        dataLabels.push(otherLabel);
      }

      setChartData(data);
      setChartLabels(dataLabels);

      // Time formatting in JavaScript, from a one-shot
      // standpoint, is very limited. So to get it to design,
      // we need to get the pieces from the Date object
      // Formatter cite: https://stackoverflow.com/questions/1643320/get-month-name-from-date/18648314#18648314
      const formatter = new Intl.DateTimeFormat('en', { month: 'long' });
      if (latestTimestamp) {
        // obs-geo timestamps are naive ISO strings representing UTC; append Z so
        // Date parses the correct instant before rendering it in local time below.
        const obsTime = new Date(`${latestTimestamp}Z`);
        const obsMonth = formatter.format(obsTime);
        const obsDate = obsTime.getDate();
        const obsYear = obsTime.getFullYear();

        setChartTime(`${obsMonth} ${obsDate}, ${obsYear}`);
      } else {
        setChartTime('');
      }
    }
  }, [observationalGeoDataResult, parameters, parameterConfigurationsResult]);

  return (
    <div className="omics-dataset-summary" style={{ width: '100%', height: '100%' }}>
      <div className="tooltip-header">
        <span className="h3">
          <div style={{ width: '400px' }}>
            {properties.platform_name}
          </div>
          <div
            className="caption-w"
            style={{ color: brandingConfig.colors.greyLight }}
          >
            <div>
              Lat:
              &nbsp;
              {info.geometry?.coordinates[1].toFixed(5)}
              &nbsp;&nbsp;
              Lon:
              &nbsp;
              {info.geometry?.coordinates[0].toFixed(5)}
            </div>
            {chartTime.length > 0 && (
              <div className="sample-date">
                <ClockCircleOutlined />
                &nbsp;
                Most recent sample
                &nbsp;
                <span>
                  (
                  {chartTime}
                  )
                </span>
              </div>
            )}
          </div>
        </span>
      </div>
      {/* This loading component seems to be causing issues with the tooltip persisting... */}
      {/* {observationalGeoDataIsLoading && (
        <div style={{
          position: 'relative',
        }}
        >
          <Loading />
        </div>
      )} */}
      {observationalGeoDataResult && !observationalGeoDataIsLoading && (
        <div className="omics-chart-wrapper">
          {chartData.length > 0 && (
            <Chart options={chartOptions} series={chartData} type="donut" height="100%" width={400} />
          )}
        </div>
      )}
    </div>
  );
};

OmicsDatasetSummary.propTypes = {
  info: PropTypes.object,
  filters: PropTypes.object,
  parameters: PropTypes.array,
};

OmicsDatasetSummary.defaultProps = {
  info: {},
  filters: {},
  parameters: [],
};

export default memo(OmicsDatasetSummary);
